<script setup>
import { ref, computed } from 'vue'
import TeachingMethodCard from './TeachingMethodCard.vue'

const props = defineProps({
  methods: {
    type: Array,
    required: true
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: 'Teaching Methods'
  },
  description: {
    type: String,
    default: 'Select the teaching methods you want to offer'
  },
  required: {
    type: Boolean,
    default: false
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  size: {
    type: String,
    default: 'large' // 'large' or 'small'
  },
  columns: {
    type: Number,
    default: 2 // number of columns in grid
  }
})

const emit = defineEmits(['update:modelValue'])

const localErrors = ref({})

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

// Sort methods by ID
const sortedMethods = computed(() => {
  return [...props.methods].sort((a, b) => a.id - b.id)
})

const gridClasses = computed(() => {
  return `grid grid-cols-1 md:grid-cols-${props.columns} gap-6`
})

const isSelected = (id) => {
  return props.modelValue.includes(id)
}

const toggleMethod = (id) => {
  const selected = [...props.modelValue]
  const index = selected.indexOf(id)

  if (index === -1) {
    selected.push(id)
  } else {
    selected.splice(index, 1)
  }

  emit('update:modelValue', selected)

  // Clear error when user makes a selection
  if (localErrors.value.teaching_methods) {
    delete localErrors.value.teaching_methods
  }
}

const validate = () => {
  const errors = {}

  if (props.required && props.modelValue.length === 0) {
    errors.teaching_methods = 'Please select at least one teaching method'
  }

  localErrors.value = errors
  return Object.keys(errors).length === 0
}

defineExpose({ validate })
</script>

<template>
  <div class="md:bg-white md:rounded-2xl md:shadow-lg p-0 md:p-10">
    <div class="mb-8">
      <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">
        {{ title }} <span v-if="required" class="text-red-500">*</span>
      </h2>
      <p class="text-gray-600">{{ description }}</p>

      <!-- Error Message -->
      <div v-if="displayErrors.teaching_methods || displayErrors.teaching_method_ids" class="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-red-600 text-xl mt-0.5">error</span>
          <div>
            <p class="text-red-600 font-semibold text-base">{{ displayErrors.teaching_methods || displayErrors.teaching_method_ids }}</p>
            <p class="text-red-700 text-sm mt-1">Choose at least one teaching method from the options below.</p>
          </div>
        </div>
      </div>
    </div>

    <div :class="gridClasses">
      <TeachingMethodCard
        v-for="method in sortedMethods"
        :key="method.id"
        :method="method"
        :selected="isSelected(method.id)"
        :size="size"
        @toggle="toggleMethod"
      />
    </div>
  </div>
</template>
